import type { PageDestination } from 'features/knowledgeDocumentViewer/graph/nodePageDestinations'
import type { SystemDocumentEntry } from 'features/knowledgeDocumentViewer/types/systemDocumentEntries'
import { compareRevisionCodes } from 'features/knowledgeDocumentViewer/utils/revisionHelpers'

import type { RelatedDocumentRow } from './RelatedDocumentsTable'

export type NodeDocumentRow = RelatedDocumentRow & { pageNumber: number }

export function latestRevisionDocumentRows(
  documentIds: readonly string[],
  systemDocuments: readonly SystemDocumentEntry[],
  currentDocumentId: string
): RelatedDocumentRow[] {
  const byStableId = new Map<string, SystemDocumentEntry>()
  for (const wrapper of systemDocuments) {
    const stableId = wrapper.entry.data.document_id
    if (stableId) {
      byStableId.set(stableId, wrapper)
    }
  }
  const byKey = new Map<string, RelatedDocumentRow[]>()
  for (const bareId of documentIds) {
    const data = byStableId.get(bareId)?.entry.data
    const openId = data?.document_id ?? bareId
    const label =
      data?.document_title ||
      data?.title ||
      data?.drawing_number ||
      data?.filename ||
      bareId
    const key = data?.drawing_number?.trim() || bareId
    const revision: RelatedDocumentRow = {
      key,
      openId,
      label,
      revisionCode: data?.revision_code ?? '',
      isCurrent: openId === currentDocumentId || bareId === currentDocumentId,
    }
    const family = byKey.get(key)
    if (family) family.push(revision)
    else byKey.set(key, [revision])
  }
  return [...byKey.values()].map(
    (revisions) =>
      [...revisions].sort(
        (a, b) => -compareRevisionCodes(a.revisionCode, b.revisionCode)
      )[0]
  )
}

export function nodeDocumentRows(
  destinations: readonly PageDestination[],
  systemDocuments: readonly SystemDocumentEntry[],
  currentDocumentId: string
): NodeDocumentRow[] {
  const firstPageByDocumentId = new Map<string, number>()
  for (const { documentId, pageNumber } of destinations) {
    const known = firstPageByDocumentId.get(documentId)
    firstPageByDocumentId.set(
      documentId,
      known === undefined ? pageNumber : Math.min(known, pageNumber)
    )
  }
  return latestRevisionDocumentRows(
    [...firstPageByDocumentId.keys()],
    systemDocuments,
    currentDocumentId
  ).flatMap((row) => {
    const pageNumber = firstPageByDocumentId.get(row.openId)
    return pageNumber === undefined ? [] : [{ ...row, pageNumber }]
  })
}
